import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import FileUpload from "../helper/FileUpload";
import { fetchData } from "../helper/api.interceptor";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const EditProduct = () => {
    const navigate = useNavigate();
    const { productId } = useParams();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    const [productName, setProductName] = useState("");
    const [productDesc, setProductDesc] = useState("");
    const [productSize, setProductSize] = useState("");
    const [productDetailsPage, setProductDetailsPage] = useState("");
    const [mainPrice, setMainPrice] = useState("");
    const [afterDiscountPrice, setAfterDiscountPrice] = useState("");
    const [productTypes, setProductTypes] = useState([]);
    const [heroVideo, setHeroVideo] = useState([]);
    const [productListingImage, setProductListingImage] = useState([]);

    const [projectImages, setProjectImages] = useState([]); // productImages
    const [mainImages, setMainImages] = useState([]);
    const [newArrivalListImages, setNewArrivalListImages] = useState([]);
    const [placeOrderImages, setPlaceOrderImages] = useState([]);

    const [whyChooseUs, setWhyChooseUs] = useState([{ feature_name: "", feature_dec: "" }]);

    // const [imageErrors, setImageErrors] = useState({
    //     newArrivalList: "",
    //     placeOrderImages: "",
    // });

    // 📌 FETCH PRODUCT DETAILS
    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await fetchData.get(`product/details/${productId}`);

                if (!res?.result) {
                    Swal.fire("Error", "Product not found", "error");
                    navigate("/product_details");
                    return;
                }

                const data = res.result;

                // Pre-fill state
                setProductName(data.product_name);
                setProductDesc(data.product_desc);
                setProductSize(data.product_size);
                setProductDetailsPage(data.product_details_page || "");
                setMainPrice(data.main_price);
                setAfterDiscountPrice(data.after_discount_price);
                setProductTypes(data.type || []);
                setWhyChooseUs(data.why_choose_us || []);

                const convertUrlsToObjects = (arr) => {
                    return arr?.map((url) => ({
                        preview: url, // Full URL
                        file: null, // Not a new upload
                        isExisting: true,
                    })) || [];
                };

                setProjectImages(convertUrlsToObjects(data.product_images));
                setMainImages(convertUrlsToObjects(data.main_images));
                setNewArrivalListImages(convertUrlsToObjects(data.new_arrival_list));
                setPlaceOrderImages(convertUrlsToObjects(data.place_order_images));
                setHeroVideo(
                    data.hero_video
                        ? [{ preview: data.hero_video, file: null, isExisting: true }]
                        : []
                );
                setProductListingImage(
                    data.product_listing_image
                        ? [{ preview: data.product_listing_image, file: null, isExisting: true }]
                        : []
                );
            } catch (error) {
                console.error(error);
                Swal.fire("Error", "Failed to load product", "error");
            } finally {
                setFetching(false);
            }
        };

        fetchProduct();
    }, [productId]);

    // Helpers
    const addFeature = () => {
        setWhyChooseUs([...whyChooseUs, { feature_name: "", feature_dec: "" }]);
    };

    const updateFeature = (index, field, value) => {
        const updated = [...whyChooseUs];
        updated[index][field] = value;
        setWhyChooseUs(updated);
    };

    const removeFeature = (index) => {
        if (whyChooseUs.length > 1) {
            setWhyChooseUs(whyChooseUs.filter((_, i) => i !== index));
        }
    };

    const handleTypeChange = (type) => {
        setProductTypes((prev) =>
            prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]
        );
    };

    const appendFilesToFormData = (formData, fieldName, files) => {
        if (!files) return;

        files.forEach((file) => {
            if (file instanceof File) {
                formData.append(fieldName, file);
            }
        });
    };

    // IMAGE HANDLERS
    const handleMainImagesSelect = (files) => setMainImages(files);
    // const handleFileSelect = (files) => setProjectImages(files);

    // const handleNewArrivalListSelect = (files) => {
    //     if (files.length > 2) {
    //         setImageErrors((prev) => ({
    //             ...prev,
    //             newArrivalList: "Maximum 2 images allowed",
    //         }));
    //         setNewArrivalListImages(files.slice(0, 2));
    //     } else {
    //         setImageErrors((prev) => ({ ...prev, newArrivalList: "" }));
    //         setNewArrivalListImages(files);
    //     }
    // };

    // const handlePlaceOrderImagesSelect = (files) => {
    //     if (files.length > 1) {
    //         setImageErrors((prev) => ({
    //             ...prev,
    //             placeOrderImages: "Only 1 image allowed",
    //         }));
    //         setPlaceOrderImages(files.slice(0, 1));
    //     } else {
    //         setImageErrors((prev) => ({ ...prev, placeOrderImages: "" }));
    //         setPlaceOrderImages(files);
    //     }
    // };

    // 📌 SUBMIT UPDATED PRODUCT
    const convertUrlToFile = async (url) => {
        const response = await fetch(url);
        const blob = await response.blob();
        const filename = url.split("/").pop(); // extract file name from URL
        return new File([blob], filename, { type: blob.type });
    };

    // Append all images (new + existing) as files
    const appendAllFiles = async (formData, fieldName, files) => {
        for (const f of files) {
            if (f.file) {
                // New file
                formData.append(fieldName, f.file);
            } else if (f.isExisting && f.preview) {
                // Existing image, convert to file
                const file = await convertUrlToFile(f.preview);
                formData.append(fieldName, file);
            }
        }
    };

    // 📌 SUBMIT UPDATED PRODUCT
    const handleUpdate = async () => {
        setLoading(true);

        try {
            const formData = new FormData();

            formData.append("product_name", productName);
            formData.append("product_desc", productDesc);
            formData.append("product_size", productSize);
            formData.append("product_details_page", productDetailsPage);
            formData.append("main_price", mainPrice);
            formData.append("after_discount_price", afterDiscountPrice);
            formData.append("type", JSON.stringify(productTypes));
            formData.append("why_choose_us", JSON.stringify(whyChooseUs));

            if (heroVideo[0]?.file) {
                formData.append("hero_video", heroVideo[0].file);
            }
            if (productListingImage[0]?.file) {
                formData.append("product_listing_image", productListingImage[0].file);
            }

            // Append files
            await appendAllFiles(formData, "productImages", projectImages);
            await appendAllFiles(formData, "mainImages", mainImages);
            await appendAllFiles(formData, "new_arrival_list", newArrivalListImages);
            await appendAllFiles(formData, "place_order_images", placeOrderImages);

            const result = await fetchData.postWithFormData(
                `product/update-all/${productId}`,
                formData
            );

            if (result.status_code === 200) {
                Swal.fire("Updated!", "Product updated successfully!", "success").then(() =>
                    // navigate(`/product/edit-specification/${productId}`)
                    navigate(`/product/edit-product-colorvariant/${productId}`)
                );
            } else {
                Swal.fire("Error", result.message || "Update failed", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong while updating", "error");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) return <p className="p-4">Loading product...</p>;

    return (
        <div>
            <div className="d-flex justify-content-between mb-4">
                <h6 className="fw-semibold mb-0">Edit Product</h6>
                <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                    <Icon icon="mdi:arrow-left" /> Back
                </button>
            </div>

            <div className="card">
                <div className="card-body">

                    {/* SAME FORM AS ADD PRODUCT */}
                    {/* --- COPY/PASTE ALL INPUT FIELDS FROM AddProduct --- */}
                    {/* I will include the fields directly below: */}

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Product Name</label>
                        <input
                            className="form-control"
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Product Description</label>
                        <textarea
                            className="form-control"
                            value={productDesc}
                            onChange={(e) => setProductDesc(e.target.value)}
                        ></textarea>
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Product Size</label>
                        <input
                            className="form-control"
                            value={productSize}
                            onChange={(e) => setProductSize(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Product Details Page</label>
                        <textarea
                            className="form-control"
                            rows={4}
                            value={productDetailsPage}
                            onChange={(e) => setProductDetailsPage(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">Main Price</label>
                        <input
                            type="number"
                            className="form-control"
                            value={mainPrice}
                            onChange={(e) => setMainPrice(e.target.value)}
                        />
                    </div>

                    <div className="mb-3">
                        <label className="form-label fw-semibold">After Discount Price</label>
                        <input
                            type="number"
                            className="form-control"
                            value={afterDiscountPrice}
                            onChange={(e) => setAfterDiscountPrice(e.target.value)}
                        />
                    </div>

                    {/* Checkboxes */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Product Type</label>
                        <div className="d-flex gap-4 flex-wrap">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={productTypes.includes("New Arrivals")}
                                        onChange={() => handleTypeChange("New Arrivals")}
                                    />
                                }
                                label="New Arrivals"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={productTypes.includes("Bestsellers")}
                                        onChange={() => handleTypeChange("Bestsellers")}
                                    />
                                }
                                label="Bestsellers"
                            />
                        </div>
                    </div>

                    <div className="mb-3 mt-4">
                        <FileUpload
                            label="Hero Video"
                            multiple={false}
                            maxFiles={1}
                            accept={{ "image/*": [], "video/*": [] }}
                            fileType="mixed"
                            images={heroVideo}
                            onImagesChange={setHeroVideo}
                        />
                    </div>

                    <div className="mb-3 mt-4">
                        <FileUpload
                            label="Product Listing Image"
                            multiple={false}
                            maxFiles={1}
                            images={productListingImage}
                            onImagesChange={setProductListingImage}
                        />
                    </div>

                    {/* FILE UPLOADS */}
                    {/* <FileUpload
                        label="Product Images"
                        multiple
                        onFilesSelected={handleFileSelect}
                        existingFiles={projectImages}
                    />

                    <FileUpload
                        label="Main Images"
                        multiple
                        onFilesSelected={handleMainImagesSelect}
                        existingFiles={mainImages}
                    />

                    <FileUpload
                        label="New Arrival List Images"
                        multiple
                        onFilesSelected={handleNewArrivalListSelect}
                        existingFiles={newArrivalListImages}
                        maxFiles={2}
                    />

                    <FileUpload
                        label="Place Order Images"
                        multiple={false}
                        onFilesSelected={handlePlaceOrderImagesSelect}
                        existingFiles={placeOrderImages}
                        maxFiles={1}
                    />   */}

                    {/* <hr /> */}

                    {/* Why Choose Us */}
                    <label className="form-label fw-semibold">Why Choose Us</label>
                    {whyChooseUs.map((f, index) => (
                        <div key={index} className="border p-3 mb-3 rounded">
                            <div className="row g-2">
                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Feature Name"
                                        value={f.feature_name}
                                        onChange={(e) =>
                                            updateFeature(index, "feature_name", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="col-md-5">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Feature Description"
                                        value={f.feature_dec}
                                        onChange={(e) =>
                                            updateFeature(index, "feature_dec", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="col-md-2 d-flex align-items-center">
                                    {whyChooseUs.length > 1 && (
                                        <button
                                            type="button"
                                            className="btn bg-custom"
                                            onClick={() => removeFeature(index)}
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    <button className="btn addfeature" onClick={addFeature}>
                        + Add Feature
                    </button>

                    <div className="mt-10">
                        <button
                            className="btn bg-custom px-16"
                            onClick={handleUpdate}
                            disabled={loading}
                        >
                            {loading ? "Updating..." : "Update Product"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditProduct;
